import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Icon,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Text,
  UnorderedList,
  ListItem,
  VStack,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiAlertTriangle, FiArrowLeft } from "react-icons/fi";
import type {
  AwardCriterionDto,
  EventDataDto,
  SelectionCriterionType,
  SourcingEventTemplateDto,
  TemplateContentDto,
} from "../../../../../../autogen/bff-api";
import { criteriaReplacedByTemplate, templateDefinesSection } from "../../../../../../common/library/applyTemplate";
import { useRequirementDescription } from "../../../../../../common/library/describeRequirement";
import { TypeBadge } from "../../../../../../common/library/RequirementTypeBadge";
import type { TemplateSection } from "../../../../../../common/library/templateFromEvent";
import { useProcurementTerms } from "../../../../../../common/terminology/useProcurementTerms";
import {
  adjustedSubWeights,
  awardCriteriaOf,
  chosenRows,
  chosenWeightTotal,
  contributingSections,
  everythingSelected,
  rowCount,
  sectionsDefinedBy,
  toggleAwardSubRow,
  toggleEverything,
  toggleRow,
  toggleSection,
  type TemplateSelection,
} from "../../../../../../common/library/templateSelection";
import { isExemptionMarker } from "../../../../../../common/procurement/publicProcurementOptions";
import { useStepLabel } from "../../../../../../common/sourcing-events/buyer/wizardSteps";
import type { SectionStepNumbers } from "./sectionSteps";
import { criterionTypeLabelKey } from "../../../../../../common/procurement/publicProcurementOptions";

// t('Add «{{name}}» to the procurement?') t('Back to the templates') t('Add to the procurement')
// t('Review what the template contains. The content is copied in and can be edited further — the template itself is unchanged.')
// t('The content is a standalone copy — later changes to the template do not affect this draft. Everything can be edited afterwards.')
// t('{{selected}} of {{total}}') t('lands in step {{n}}') t('replaces what is in step {{n}}')
// t('Step {{n}} is empty — the template fills it.') t('Untick {{section}} to keep your own.')
// t('Replaces the current minimum requirements:') t('Replaces the current qualification requirements:')
// t('This procurement has no step for what the template carries.')
// t('Take everything') t('Clear everything')

// t('Method') t('Lowest price') t('Best price-quality ratio')

// t('This removes the climate and environment justification (§ 5c) on the procurement.')

const checkboxSx = { ".chakra-checkbox__control:not([data-checked])": { bg: "smBackground" } };

const titleIndent = 7;

const SECTION_STEP_KEY = {
  minimum: "minimumRequirements",
  selection: "qualification",
  award: "awardCriteria",
} as const;

interface Props {
  template: SourcingEventTemplateDto;
  eventData: EventDataDto;
  stepNumbers: SectionStepNumbers;
  selection: TemplateSelection;
  onSelectionChange: (next: TemplateSelection) => void;
  onBack: () => void;
  onApply: () => void;
}

export const TemplatePreviewStep = ({
  template,
  eventData,
  stepNumbers,
  selection,
  onSelectionChange,
  onBack,
  onApply,
}: Props) => {
  const { t } = useTranslation();
  const stepLabel = useStepLabel();
  const content = template.content;

  const sections = sectionsDefinedBy(content)
    .flatMap((section) => {
      const stepNumber = stepNumbers[section];
      return stepNumber == null ? [] : [{ section, stepNumber }];
    })
    .sort((a, b) => a.stepNumber - b.stepNumber);

  const offered = sections.map(({ section }) => section);
  const canApply = contributingSections(content, selection).some((section) => offered.includes(section));

  return (
    <>
      <ModalHeader pb="4">
        <Text>{t("Add «{{name}}» to the procurement?", { name: template.name })}</Text>
        <Text fontSize="sm" fontWeight="normal" color="smMuted" mt="2">
          {t(
            "Review what the template contains. The content is copied in and can be edited further — the template itself is unchanged."
          )}
        </Text>
      </ModalHeader>
      <ModalBody borderTopWidth="1px" borderColor="smBorder" pt="5" tabIndex={-1}>
        {sections.length === 0 && (
          <Text color="smMuted" py="6" textAlign="center">
            {t("This procurement has no step for what the template carries.")}
          </Text>
        )}
        <VStack align="stretch" spacing="3">
          {sections.map(({ section, stepNumber }) => (
            <SectionCard
              key={section}
              section={section}
              label={stepLabel(SECTION_STEP_KEY[section])}
              stepNumber={stepNumber}
              content={content}
              eventData={eventData}
              selection={selection}
              onSelectionChange={onSelectionChange}
            />
          ))}
        </VStack>
        <Text fontSize="sm" color="smMuted" mt="5">
          {t(
            "The content is a standalone copy — later changes to the template do not affect this draft. Everything can be edited afterwards."
          )}
        </Text>
      </ModalBody>
      <ModalFooter borderTopWidth="1px" borderColor="smBorder" justifyContent="space-between" gap="4">
        <Button variant="ghost" leftIcon={<Icon as={FiArrowLeft} />} onClick={onBack}>
          {t("Back to the templates")}
        </Button>
        <HStack spacing="3">
          <Button
            variant="outline"
            colorScheme="teal"
            onClick={() => onSelectionChange(toggleEverything(content, selection, offered))}
          >
            {everythingSelected(content, selection, offered) ? t("Clear everything") : t("Take everything")}
          </Button>
          <Button colorScheme="teal" onClick={onApply} isDisabled={!canApply}>
            {t("Add to the procurement")}
          </Button>
        </HStack>
      </ModalFooter>
    </>
  );
};

interface SectionCardProps {
  section: TemplateSection;
  label: string;
  stepNumber: number;
  content: TemplateContentDto;
  eventData: EventDataDto;
  selection: TemplateSelection;
  onSelectionChange: (next: TemplateSelection) => void;
}

const SectionCard = ({
  section,
  label,
  stepNumber,
  content,
  eventData,
  selection,
  onSelectionChange,
}: SectionCardProps) => {
  const { t } = useTranslation();
  const isOn = selection.sections.includes(section);
  const total = rowCount(content, section);
  const chosen = chosenRows(selection, section);
  const replaces = isOn && sectionReplaces(section, content, eventData);
  const replaced = replaces ? replacedRows(section, content, eventData) : [];

  return (
    <Box
      data-testid={`template-section-${section}`}
      borderWidth="1px"
      borderColor={isOn ? "smPrimary" : "smBorder"}
      bg={isOn ? "smPrimarySofter" : "smBackground"}
      borderRadius="lg"
      p="4"
    >
      <Flex align="center" gap="3" flexWrap="wrap">
        <Checkbox
          isChecked={isOn}
          onChange={() => onSelectionChange(toggleSection(content, selection, section))}
          colorScheme="teal"
          sx={checkboxSx}
          aria-label={label}
        />
        <Text fontWeight="bold" cursor="pointer" onClick={() => onSelectionChange(toggleSection(content, selection, section))}>
          {label}
        </Text>
        {total > 0 && (
          <Badge textTransform="none" borderRadius="md" px="2" fontWeight="medium">
            {t("{{selected}} of {{total}}", { selected: chosen.length, total })}
          </Badge>
        )}
        <Text color="smMuted" fontSize="sm">
          — {replaces ? t("replaces what is in step {{n}}", { n: stepNumber }) : t("lands in step {{n}}", { n: stepNumber })}
        </Text>
      </Flex>

      {!replaces && <SectionEmptyStepLine section={section} stepNumber={stepNumber} eventData={eventData} />}

      {section === "award" ? (
        <AwardRows content={content} selection={selection} onSelectionChange={onSelectionChange} />
      ) : (
        <PlainRows
          section={section}
          content={content}
          chosen={chosen}
          onToggleRow={(index) => onSelectionChange(toggleRow(selection, section, index))}
        />
      )}

      {section === "award" && isOn && (
        <AwardWarnings
          content={content}
          eventData={eventData}
          selection={selection}
          stepNumber={stepNumber}
        />
      )}

      {replaced.length > 0 && <ReplacesBox section={section} label={label} rows={replaced} />}
    </Box>
  );
};

interface ReplacedRow {
  title: string;
  detail: string | null;
}

const sectionReplaces = (
  section: TemplateSection,
  content: TemplateContentDto,
  eventData: EventDataDto
): boolean => {
  if (!templateDefinesSection(content, section)) return false;
  if (section === "minimum") return (eventData.minimumRequirements?.length ?? 0) > 0;
  if (section === "selection") return (eventData.selectionCriteria?.length ?? 0) > 0;
  return criteriaReplacedByTemplate(content, eventData).length > 0;
};

const replacedRows = (
  section: TemplateSection,
  content: TemplateContentDto,
  eventData: EventDataDto
): ReplacedRow[] => {
  if (!sectionReplaces(section, content, eventData)) return [];

  if (section === "minimum") {
    return (eventData.minimumRequirements ?? []).map((r) => ({ title: r.title, detail: null }));
  }
  if (section === "selection") {
    return (eventData.selectionCriteria ?? []).map((c) => ({ title: c.name ?? "", detail: null }));
  }
  return criteriaReplacedByTemplate(content, eventData)
    .filter((c) => !isExemptionMarker(c.type))
    .map((c) => ({ title: c.name ?? c.type, detail: null }));
};

const ReplacesBox = ({
  section,
  label,
  rows,
}: {
  section: TemplateSection;
  label: string;
  rows: ReplacedRow[];
}) => {
  const { t } = useTranslation();
  const terms = useProcurementTerms();
  const heading =
    section === "award"
      ? terms.replacesCurrentAwardCriteria
      : section === "minimum"
        ? t("Replaces the current minimum requirements:")
        : t("Replaces the current qualification requirements:");

  return (
    <Box
      mt="3"
      ml={titleIndent}
      bg="smWarningSoft"
      borderWidth="1px"
      borderColor="smWarningSoftBorder"
      borderRadius="md"
      px="3"
      py="2"
      color="smWarning"
    >
      <Flex align="center" gap="2">
        <Icon as={FiAlertTriangle} flexShrink={0} />
        <Text fontSize="sm" fontWeight="semibold">
          {heading}
        </Text>
      </Flex>
      <UnorderedList fontSize="sm" mt="1" ml="7" spacing="0.5">
        {rows.map((row, index) => (
          <ListItem key={index}>
            {row.title}
            {row.detail && <Text as="span" opacity={0.8}>{` · ${row.detail}`}</Text>}
          </ListItem>
        ))}
      </UnorderedList>
      <Text fontSize="sm" mt="2">
        {t("Untick {{section}} to keep your own.", { section: label })}
      </Text>
    </Box>
  );
};

const SectionEmptyStepLine = ({
  section,
  stepNumber,
  eventData,
}: {
  section: TemplateSection;
  stepNumber: number;
  eventData: EventDataDto;
}) => {
  const { t } = useTranslation();
  const hasExisting =
    section === "minimum"
      ? (eventData.minimumRequirements?.length ?? 0) > 0
      : section === "selection"
        ? (eventData.selectionCriteria?.length ?? 0) > 0
        : eventData.awardCriteria != null;

  if (hasExisting) return null;

  return (
    <Text color="smMuted" fontSize="sm" mt="2" ml={titleIndent}>
      {t("Step {{n}} is empty — the template fills it.", { n: stepNumber })}
    </Text>
  );
};

const PlainRows = ({
  section,
  content,
  chosen,
  onToggleRow,
}: {
  section: TemplateSection;
  content: TemplateContentDto;
  chosen: number[];
  onToggleRow: (index: number) => void;
}) => {
  const { t } = useTranslation();
  const describe = useRequirementDescription();
  const rows: { title: string; summary: string; type?: SelectionCriterionType }[] =
    section === "minimum"
      ? (content.minimumRequirements ?? []).map((r) => ({ title: r.title, summary: describe.minimum(r) }))
      : (content.selectionCriteria ?? []).map((c) => ({
          title: c.name ?? "",
          summary: describe.selection(c, { titleShowsType: true }),
          type: c.type,
        }));

  if (rows.length === 0) return null;

  return (
    <VStack align="stretch" spacing="3" mt="3">
      {rows.map((row, index) => (
        <Flex key={index} align="flex-start" gap="3">
          <Checkbox
            isChecked={chosen.includes(index)}
            onChange={() => onToggleRow(index)}
            colorScheme="teal"
            sx={checkboxSx}
            aria-label={row.title}
            mt="0.5"
          />
          <Box flex="1" minW="0">
            <Flex align="center" gap="2" flexWrap="wrap">
              <Text fontSize="sm" fontWeight="medium">
                {row.title}
              </Text>
              {row.type && <TypeBadge>{t(row.type)}</TypeBadge>}
            </Flex>
            {row.summary && (
              <Text fontSize="sm" color="smMuted" mt="0.5">
                {row.summary}
              </Text>
            )}
          </Box>
        </Flex>
      ))}
    </VStack>
  );
};

const AwardRows = ({
  content,
  selection,
  onSelectionChange,
}: {
  content: TemplateContentDto;
  selection: TemplateSelection;
  onSelectionChange: (next: TemplateSelection) => void;
}) => {
  const { t } = useTranslation();
  const criteria = awardCriteriaOf(content);
  const model = content.awardCriteria?.bestPriceQualityRatio
    ? t("Best price-quality ratio")
    : t("Lowest price");


  return (
    <>
      <Text fontSize="sm" mt="3" ml={titleIndent}>
        {t("Method")}:{" "}
        <Text as="span" fontWeight="semibold">
          {model}
        </Text>
      </Text>
      {criteria.length > 0 && (
        <VStack align="stretch" spacing="1" mt="2">
          {criteria.map((criterion, index) => {
            const subs = criterion.subCriteria ?? [];
            const chosenSubs = selection.awardSub[index] ?? [];
            const subWeights = adjustedSubWeights(subs, chosenSubs);
            return (
              <Box key={index}>
                <Flex align="center" gap="3">
                  <Checkbox
                    isChecked={selection.award.includes(index)}
                    onChange={() => onSelectionChange(toggleRow(selection, "award", index))}
                    colorScheme="teal"
                    sx={checkboxSx}
                    aria-label={criterion.name || criterion.type}
                  />
                  <AwardRowText criterion={criterion} />
                </Flex>
                {subs.length > 0 && (
                  <VStack
                    align="stretch"
                    spacing="1"
                    mt="1"
                    ml={titleIndent}
                    pl="3"
                    borderLeftWidth="2px"
                    borderColor="smBorder"
                  >
                    {subs.map((sub, subIndex) => {
                      const weight = subWeights[subIndex] ?? sub.weight?.number;
                      return (
                        <Flex key={subIndex} align="center" gap="3">
                          <Checkbox
                            isChecked={chosenSubs.includes(subIndex)}
                            onChange={() => onSelectionChange(toggleAwardSubRow(selection, index, subIndex))}
                            colorScheme="teal"
                            sx={checkboxSx}
                            aria-label={sub.name ?? undefined}
                          />
                          <Text fontSize="sm">{sub.name}</Text>
                          {weight != null && (
                            <Text fontSize="sm" color="smMuted">
                              · {t("weight {{n}} %", { n: weight })}
                            </Text>
                          )}
                        </Flex>
                      );
                    })}
                  </VStack>
                )}
              </Box>
            );
          })}
        </VStack>
      )}
    </>
  );
};

const AwardRowText = ({ criterion }: { criterion: AwardCriterionDto }) => {
  const { t } = useTranslation();
  const subCount = (criterion.subCriteria ?? []).length;
  return (
    <Flex align="center" gap="2" flexWrap="wrap">
      <Text fontSize="sm">{criterion.name || t(criterionTypeLabelKey(criterion.type))}</Text>
      {criterion.weight?.number != null && (
        <Text fontSize="sm" color="smMuted">
          · {t("weight {{n}} %", { n: criterion.weight.number })}
        </Text>
      )}
      {subCount > 0 && (
        <Text fontSize="sm" color="smMuted">
          · {t("{{count}} sub-criterion", { count: subCount })}
        </Text>
      )}
    </Flex>
  );
};

const AwardWarnings = ({
  content,
  eventData,
  selection,
  stepNumber,
}: {
  content: TemplateContentDto;
  eventData: EventDataDto;
  selection: TemplateSelection;
  stepNumber: number;
}) => {
  const { t } = useTranslation();
  const terms = useProcurementTerms();
  const criteria = awardCriteriaOf(content);
  const total = chosenWeightTotal(content, selection);
  const losesExemption = criteriaReplacedByTemplate(content, eventData).some((c) => isExemptionMarker(c.type));

  return (
    <>
      {criteria.length > 0 && total !== 100 && (
        <Box
          mt="3"
          ml={titleIndent}
          bg="smWarningSoft"
          borderWidth="1px"
          borderColor="smWarningSoftBorder"
          borderRadius="md"
          px="3"
          py="2"
        >
          <Text fontSize="sm" color="smWarning">
            {terms.selectedCriteriaWeightTotal(total, stepNumber)}
          </Text>
        </Box>
      )}
      {losesExemption && (
        <Flex align="center" gap="2" mt="3" ml={titleIndent} color="smWarning">
          <Icon as={FiAlertTriangle} flexShrink={0} />
          <Text fontSize="sm">
            {t("This removes the climate and environment justification (§ 5c) on the procurement.")}
          </Text>
        </Flex>
      )}
    </>
  );
};
